<template>
    <div>
        <div class="field">
            <div class="control">
                <label class="checkbox">
                    <input type="checkbox" v-model="draft">
                    Draft
                </label>
            </div>
        </div>
        <div class="field">
            <label class="label">Name</label>
            <div class="control">
                <input class="input" type="text" placeholder="Name" v-model="name">
            </div>
        </div>
        <div class="field">
            <label class="label">Description<br><span class="is-hint-text">(displayed under the page title in Google search, will override the site wide description)</span></label>
            <div class="control">
                <textarea class="textarea" placeholder="Description" rows="3" v-model="description"></textarea>
            </div>
        </div>
        <div class="field">
            <label class="label">Keywords<br><span class="is-hint-text">(will be merged with site wide keywords, press enter to add a new keyword)</span></label>
            <div class="control">
                <b-taginput placeholder="Keywords" v-model="keywords" />
            </div>
        </div>
        <div class="field">
            <label class="label">Notes Title<br><span class="is-hint-text">(displayed on the home page)</span></label>
            <div class="control">
                <input class="input" type="text" placeholder="Notes Title" v-model="notesTitle">
            </div>
        </div>
        <div class="field">
            <div class="control">
                <button class="button" @click="applyToAll">Apply to All</button>
            </div>
        </div>
        <div class="field">
            <label class="label">Notes<br><span class="is-hint-text">(displayed on the home page and in the projects archive, use newlines to separate items)</span></label>
            <div class="control">
                <textarea class="textarea" placeholder="Notes" v-model="notes"></textarea>
            </div>
        </div>
        <div class="field">
            <div class="label">Images<br><span class="is-hint-text">(the first image may have a portrait version)</span></div>
            <div class="control">
                <ul class="image-list">
                    <draggable v-model="images" :options="{handle: '.handle'}" @start="drag=true" @end="drag=false">
                        <li v-for="(image, i) of images" :key="image['.key']" class="box level is-mobile">
                            <div class="level-item handle">
                                <font-awesome-icon icon="bars" />
                            </div>
                            <article class="level-item media">
                                <figure class="media-left">
                                    <div @click="showImageModal(image, 'landscape')" class="image box is-ar-3by2"
                                       :style="{backgroundImage: image.landscapeUrl ? `url('${contentUrlPrefix + image.landscapeUrl}')` : 'none'}">
                                        <div v-if="!image.landscapeUrl" class="has-centered-content">
                                            <font-awesome-icon icon="hand-pointer" />
                                        </div>
                                    </div>
                                    <div v-if="i === 0" @click="showImageModal(image, 'portrait')" class="image box is-ar-2by3"
                                         :style="{backgroundImage: image.portraitUrl ? `url('${contentUrlPrefix + image.portraitUrl}')` : 'none'}">
                                        <div v-if="!image.portraitUrl" class="has-centered-content">
                                            <font-awesome-icon icon="hand-pointer" />
                                        </div>
                                    </div>
                                </figure>
                                <div class="media-content">
                                    <div class="field">
                                        <div class="control">
                                            <input class="input" type="text" placeholder="Title (only shown on pure information pages)" v-model="image.title">
                                        </div>
                                    </div>
                                    <div class="field">
                                        <div class="control">
                                            <textarea class="textarea" placeholder="Description"
                                                      v-model="image.description" rows="2"></textarea>
                                        </div>
                                    </div>
                                    <div class="field">
                                        <div class="control">
                                            <label class="checkbox">
                                                <input type="checkbox" v-model="image.darkInformation">
                                                Dark Information
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div class="media-right">
                                    <button class="delete" @click="removeImage(image['.key'])"></button>
                                </div>
                            </article>
                        </li>
                    </draggable>
                </ul>
                <button class="button" @click="addImage" :style="{marginTop: `${images.length > 0 ? 1 : 0}rem`}">Add Image</button>
            </div>
        </div>
        <div class="field is-grouped">
            <div class="control">
                <button class="button is-primary" :disabled="!valid" @click="handle">{{$route.params.key ? 'Update' : 'Create'}}</button>
            </div>
            <div v-if="$route.params.key" class="control">
                <button class="button is-danger is-outlined" @click="removeModalShown = true">Remove</button>
            </div>
        </div>
        <transition name="modal">
            <remove-modal v-if="removeModalShown"
                          type="Project"
                          :id="$route.params.key"
                          @close="removeModalShown = false"
                          @remove="remove"/>
        </transition>
        <transition name="modal">
            <image-modal v-show="imageModalShown"
                         v-model="imageModalUrl"
                         @close="imageModalShown = false"
                         @select="selectImage"/>
        </transition>
    </div>
</template>

<script>
    import Draggable from 'vuedraggable';
    import {mapState} from 'vuex';
    import {PUSH_MESSAGE} from "../store";
    import RemoveModal from '../components/RemoveModal';
    import ImageModal from '../components/ImageModal';
    import { generateKey } from "../utils";
    import { contentUrlPrefix, triggerSiteBuild } from "../firebase";

    export default {
        name: "project",
        data() {
            return {
                contentUrlPrefix,
                draft: false,
                name: "",
                description: "",
                keywords: [],
                notesTitle: "",
                notes: "",
                images: [],
                removeModalShown: false,
                imageModalShown: false,
                imageModalUrl: '',
                imageModalKey: '',
                imageModalOrientation: ''
            }
        },
        computed: {
            ...mapState(["projects", "photos"]),
            valid() {
                let imagesValid = true;
                for(let image of this.images) {
                    if(!(image.landscapeUrl || image.description)) {
                        imagesValid = false;
                        break;
                    }
                }
                return !!this.name && this.images.length > 0 && !!((this.images[0] || {}).landscapeUrl) && imagesValid;
            }
        },
        watch: {
            projects: {
                handler(projects) {
                    // noinspection JSUnresolvedVariable
                    if (projects && this.$route.params.key) {
                        // noinspection JSUnresolvedVariable
                        const item = projects.find(item => item['.key'] === this.$route.params.key);
                        if (item) {
                            this.name = item.name;
                            this.draft = item.draft || false;
                            this.description = item.description || '';
                            this.keywords = item.keywords || [];
                            this.notesTitle = item.notesTitle || '';
                            this.notes = item.notes || '';
                            this.images = [];
                            for(let key of Object.keys(item.images)) {
                                let image = item.images[key];
                                this.images.push({
                                    '.key': key,
                                    landscapeUrl: image.url || '',
                                    portraitUrl: image.portraitUrl || '',
                                    title: image.title || '',
                                    description: image.description || '',
                                    darkInformation: !!image.darkInformation
                                });
                            }
                        }
                    }
                },
                immediate: true
            }
        },
        methods: {
            applyToAll() {
                // noinspection JSUnresolvedVariable
                this.projects
                    //.filter(project => project[".key"] !== this.$route.params.key)
                    .forEach(project => {
                        this.$store.$firebaseRefs.projects
                            .child(project[".key"])
                            .update({
                                notesTitle: this.notesTitle
                            });
                    });
                // noinspection JSIgnoredPromiseFromCall
                this.$store.dispatch(PUSH_MESSAGE, {
                    title: "Success",
                    message: "Applied to all!",
                    type: "success"
                });
            },
            addImage() {
                this.images.push({
                    '.key': generateKey(),
                    landscapeUrl: '',
                    portraitUrl: '',
                    title: '',
                    description: '',
                    darkInformation: false
                });
            },
            removeImage(key) {
                this.images = this.images.filter(image => image['.key'] !== key);
            },
            showImageModal(image, orientation) {
                this.imageModalUrl = orientation === 'landscape' ? image.landscapeUrl : image.portraitUrl;
                this.imageModalKey = image['.key'];
                this.imageModalOrientation = orientation;
                this.imageModalShown = true;
            },
            selectImage() {
                let image = this.images.find(image => image['.key'] === this.imageModalKey);
                if(this.imageModalOrientation === 'landscape') {
                    image.landscapeUrl = this.imageModalUrl;
                } else {
                    image.portraitUrl = this.imageModalUrl;
                }
            },
            handle() {
                // noinspection JSUnresolvedVariable
                const updating = !!this.$route.params.key;
                // noinspection JSUnresolvedVariable
                let key = this.$route.params.key;

                const payload = {
                    draft: this.draft,
                    name: this.name,
                    description: this.description,
                    keywords: this.keywords,
                    notesTitle: this.notesTitle,
                    notes: this.notes
                }

                if (updating) {
                    this.$store.$firebaseRefs.projects.child(key).update(payload);
                } else {
                    let newRef = this.$store.$firebaseRefs.projects.push(payload);
                    key = newRef.key;
                }

                const imagesRef = this.$store.$firebaseRefs.projects
                    .child(key)
                    .child("images");
                imagesRef.remove();
                for(let image of this.images) {
                    const landscapePhoto = this.photos.find(photo => photo.url === image.landscapeUrl);
                    imagesRef.push({
                        url: image.landscapeUrl,
                        portraitUrl: image.portraitUrl,
                        title: image.title,
                        description: image.description,
                        aspectRatio: landscapePhoto ? landscapePhoto.width / landscapePhoto.height : 0,
                        width: landscapePhoto ? landscapePhoto.width : 0,
                        height: landscapePhoto ? landscapePhoto.height : 0,
                        darkInformation: !!image.darkInformation
                    });
                }

                triggerSiteBuild(`${updating ? 'Updated' : 'Created'} ${this.name}`);
                // noinspection JSIgnoredPromiseFromCall
                this.$store.dispatch(PUSH_MESSAGE, {
                    title: "Success",
                    message: `Project ${updating ? 'updated' : 'created'}! It may take up to 5 minutes for changes to be visible.`,
                    type: "success"
                });

                this.$router.push("/");
            },
            remove(key) {
                this.$store.$firebaseRefs.projects
                    .child(key)
                    .remove();

                triggerSiteBuild(`Deleted ${this.name}`);
                // noinspection JSIgnoredPromiseFromCall
                this.$store.dispatch(PUSH_MESSAGE, {
                    title: "Success",
                    message: "Project removed! It may take up to 5 minutes for changes to be visible.",
                    type: "success"
                });
                this.$router.push("/");
            }
        },
        mounted() {
            document.title = `CMS - ${this.name || 'New Project'}`;
        },
        components: {
            RemoveModal,
            ImageModal,
            Draggable
        }
    }
</script>

<style lang="scss">
    @import "~bulma/sass/utilities/initial-variables.sass";

    .image-list {
        li.box {
            padding-left: 12px;
            padding-right: 5px;
            margin-bottom: 1.5rem;

            &:last-child {
                margin-bottom: 0;
            }
        }

        .level.is-mobile .level-item.handle {
            color: $grey;
            flex-grow: 0;
        }

        //noinspection CssOptimizeSimilarProperties
        .media-left {
            .image {
                display: inline-block;

                background-position: center;
                background-size: cover;
                background-color: $grey-light;

                margin: 0;

                height: 120px;

                cursor: pointer;

                &:not(:last-child) {
                    margin-right: 1rem;
                }

                &.is-ar-3by2 {
                    width: 180px;
                }

                &.is-ar-2by3 {
                    width: 80px;
                }

                position: relative;

                > div {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;

                    svg {
                        color: $white;
                    }
                }
            }
        }
    }
</style>